import { useQuery, useQueryClient } from '@tanstack/react-query';

import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import { AddCircle, Delete, Edit, Refresh } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Paper,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { useApiClient, useIsMobile, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { AgentClient, AgentModel, ObjectRoleMatrixModel, PermissionAdministrationClient, PermissionModel, RoleModel, TenantUserModel, UserClient } from '../../api/api';
import useDebouncedCallback from '../../common/hooks/useDebouncedCallback';

type EditableKind = 'role' | 'permission';

type EditableDialogState = { kind: EditableKind; mode: 'create'; item?: undefined } | { kind: EditableKind; mode: 'edit'; item: RoleModel | PermissionModel };

type ObjectDialogState = { objectId: string; name: string; type: string };

type PendingKeysSetter = Dispatch<SetStateAction<Set<string>>>;
type RoleMatrixEntry = { id: string; name: string; description?: string };

const emptyGuid = '00000000-0000-0000-0000-000000000000';
const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const emptyRoles: RoleModel[] = [];
const emptyPermissions: PermissionModel[] = [];
const filterDebounceMs = 300;
const objectTypeOptions = ['User', 'Agent'];

const getRolePermissionKey = (roleId: string, permissionId: string) => `${roleId}:${permissionId}`;
const getObjectRoleKey = (objectId: string, roleId: string) => `${objectId}:${roleId}`;
const getObjectNameKey = (objectId: string) => objectId.toLowerCase();

function setPendingKey(setter: PendingKeysSetter, key: string, pending: boolean) {
	setter((current) => {
		const next = new Set(current);
		if (pending) {
			next.add(key);
		} else {
			next.delete(key);
		}
		return next;
	});
}

async function runPendingAction(
	pendingKeys: Set<string>,
	setPendingKeys: PendingKeysSetter,
	key: string,
	action: () => Promise<void>,
	onError: (message: string) => void,
) {
	if (pendingKeys.has(key)) {
		return;
	}

	setPendingKey(setPendingKeys, key, true);
	try {
		await action();
	} catch (error) {
		onError(getErrorMessage(error));
	} finally {
		setPendingKey(setPendingKeys, key, false);
	}
}

function setAssignment(ids: string[] | undefined, id: string, assigned: boolean) {
	const current = ids ?? [];
	if (assigned) {
		return current.some((item) => item === id) ? current : [...current, id];
	}

	return current.filter((item) => item !== id);
}

function filterNamedRows<T extends { name?: string | undefined; description?: string | undefined }>(rows: T[], filter: string) {
	return filter ? rows.filter((row) => matchesNamedRow(row, filter)) : rows;
}

function matchesNamedRow(row: { name?: string | undefined; description?: string | undefined }, filter: string) {
	return (row.name?.toLowerCase().includes(filter.toLowerCase()) ?? false) || (row.description?.toLowerCase().includes(filter.toLowerCase()) ?? false);
}

function filterRolePermissionMatrix(roles: RoleModel[], permissions: PermissionModel[], filter: string) {
	if (!filter) {
		return { roles, permissions };
	}

	const matchingRoles = filterNamedRows(roles, filter);
	const matchingPermissions = filterNamedRows(permissions, filter);
	if (matchingRoles.length === 0 && matchingPermissions.length === 0) {
		return { roles: emptyRoles, permissions: emptyPermissions };
	}

	return {
		roles: matchingRoles.length > 0 ? matchingRoles : roles,
		permissions: matchingPermissions.length > 0 ? matchingPermissions : permissions,
	};
}

function matchesObjectRow(
	row: { objectId: string; objectName?: string | undefined; objectType?: string | undefined },
	objectNames: Record<string, string | undefined>,
	objectTypes: Record<string, string | undefined>,
	filter: string,
) {
	if (!filter) {
		return true;
	}

	const value = filter.toLowerCase();
	const objectKey = getObjectNameKey(row.objectId);
	const objectName = row.objectName || objectNames[objectKey] || '';
	const objectType = row.objectType || objectTypes[objectKey] || '';

	return row.objectId.toLowerCase().includes(value) || objectName.toLowerCase().includes(value) || objectType.toLowerCase().includes(value);
}

export default function PermissionAdministrationView() {
	const client = useApiClient(PermissionAdministrationClient);
	const userClient = useApiClient(UserClient);
	const agentClient = useApiClient(AgentClient);
	const toastService = useToastService();
	const queryClient = useQueryClient();
	const isMobile = useIsMobile();

	const [tab, setTab] = useState(0);
	const [dialogState, setDialogState] = useState<EditableDialogState | null>(null);
	const [objectDialog, setObjectDialog] = useState<ObjectDialogState | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ kind: EditableKind; item: RoleModel | PermissionModel } | null>(null);
	const [objectIdInput, setObjectIdInput] = useState('');
	const [objectNameInput, setObjectNameInput] = useState('');
	const [objectTypeInput, setObjectTypeInput] = useState('');
	const [objectIds, setObjectIds] = useState<string[]>([]);
	const [objectNames, setObjectNames] = useState<Record<string, string | undefined>>({});
	const [objectTypes, setObjectTypes] = useState<Record<string, string | undefined>>({});
	const [roleFilter, setRoleFilter] = useState('');
	const [permissionFilter, setPermissionFilter] = useState('');
	const [objectFilter, setObjectFilter] = useState('');
	const [rolePermissionMatrixFilter, setRolePermissionMatrixFilter] = useState('');
	const [debouncedRoleFilter, setDebouncedRoleFilter] = useState('');
	const [debouncedPermissionFilter, setDebouncedPermissionFilter] = useState('');
	const [debouncedObjectFilter, setDebouncedObjectFilter] = useState('');
	const [debouncedRolePermissionMatrixFilter, setDebouncedRolePermissionMatrixFilter] = useState('');
	const [addingObject, setAddingObject] = useState(false);
	const [pendingRolePermissionKeys, setPendingRolePermissionKeys] = useState<Set<string>>(() => new Set());
	const [pendingObjectRoleKeys, setPendingObjectRoleKeys] = useState<Set<string>>(() => new Set());

	const updateRoleFilter = useDebouncedCallback((value: string) => setDebouncedRoleFilter(value.trim()), filterDebounceMs);
	const updatePermissionFilter = useDebouncedCallback((value: string) => setDebouncedPermissionFilter(value.trim()), filterDebounceMs);
	const updateObjectFilter = useDebouncedCallback((value: string) => setDebouncedObjectFilter(value.trim()), filterDebounceMs);
	const updateRolePermissionMatrixFilter = useDebouncedCallback((value: string) => setDebouncedRolePermissionMatrixFilter(value.trim()), filterDebounceMs);

	const roleFilterValue = debouncedRoleFilter;
	const permissionFilterValue = debouncedPermissionFilter;
	const objectFilterValue = debouncedObjectFilter;
	const rolePermissionMatrixFilterValue = debouncedRolePermissionMatrixFilter;

	const handleRoleFilterChange = (value: string) => {
		setRoleFilter(value);
		updateRoleFilter(value);
	};

	const handlePermissionFilterChange = (value: string) => {
		setPermissionFilter(value);
		updatePermissionFilter(value);
	};

	const handleObjectFilterChange = (value: string) => {
		setObjectFilter(value);
		updateObjectFilter(value);
	};

	const handleRolePermissionMatrixFilterChange = (value: string) => {
		setRolePermissionMatrixFilter(value);
		updateRolePermissionMatrixFilter(value);
	};

	const rolesQuery = useQuery({
		queryKey: ['permission-admin', 'roles', roleFilterValue],
		queryFn: () => client.getRoles(roleFilterValue),
	});

	const permissionsQuery = useQuery({
		queryKey: ['permission-admin', 'permissions', permissionFilterValue],
		queryFn: () => client.getPermissions(permissionFilterValue),
	});

	const allRolesQuery = useQuery({
		queryKey: ['permission-admin', 'roles', ''],
		queryFn: () => client.getRoles(undefined),
		enabled: tab === 1 || tab === 2,
	});

	const usersQuery = useQuery({
		queryKey: ['permission-admin', 'users'],
		queryFn: () => getAllUsers(userClient),
		enabled: tab === 1,
	});

	const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
	const userIds = useMemo(() => users.map((user) => user.id), [users]);
	const userRoleEntries = useMemo<RoleMatrixEntry[]>(
		() => users.map((user) => ({ id: user.id, name: user.displayName || `${user.givenName ?? ''} ${user.surname ?? ''}`.trim() || user.email, description: user.email })),
		[users],
	);
	const agentsQuery = useQuery({
		queryKey: ['permission-admin', 'agents'],
		queryFn: () => getAllAgents(agentClient),
		enabled: tab === 2,
	});
	const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
	const agentIds = useMemo(() => agents.map((agent) => agent.id), [agents]);

	const objectMatrixQuery = useQuery({
		queryKey: ['permission-admin', 'user-role-matrix', userIds],
		queryFn: () =>
			client.getObjectRoleMatrix({
				objectIds: userIds,
			}),
		enabled: tab === 1 && usersQuery.isSuccess,
	});

	const agentMatrixQuery = useQuery({
		queryKey: ['permission-admin', 'agent-role-matrix', agentIds],
		queryFn: () =>
			client.getObjectRoleMatrix({
				objectIds: agentIds,
			}),
		enabled: tab === 2 && agentsQuery.isSuccess,
	});

	const roles = rolesQuery.data ?? emptyRoles;
	const objectRoles = allRolesQuery.data ?? emptyRoles;
	const permissions = permissionsQuery.data ?? emptyPermissions;
	const isLoading = rolesQuery.isLoading || permissionsQuery.isLoading;

	const rolePermissionIds = useMemo(() => {
		const map = new Map<string, Set<string>>();
		roles.forEach((role) => map.set(role.id, new Set(role.permissionIds ?? [])));
		return map;
	}, [roles]);

	const rolePermissionMatrix = useMemo(
		() => filterRolePermissionMatrix(roles, permissions, rolePermissionMatrixFilterValue),
		[roles, permissions, rolePermissionMatrixFilterValue],
	);

	const refreshAll = () => {
		queryClient.invalidateQueries({ queryKey: ['permission-admin'] });
	};

	const handleToggleRolePermission = async (roleId: string, permissionId: string, checked: boolean) => {
		const key = getRolePermissionKey(roleId, permissionId);
		await runPendingAction(
			pendingRolePermissionKeys,
			setPendingRolePermissionKeys,
			key,
			async () => {
				const updatedRole = checked ? await client.assignPermissionToRole(roleId, permissionId) : await client.unassignPermissionFromRole(roleId, permissionId);

				queryClient.setQueryData<RoleModel[]>(['permission-admin', 'roles', roleFilterValue], (current) =>
					current?.map((role) =>
						role.id === roleId ? (updatedRole ?? { ...role, permissionIds: setAssignment(role.permissionIds, permissionId, checked) }) : role,
					),
				);
				await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'roles'], refetchType: 'none' });
			},
			(message) => toastService.error(message),
		);
	};

	const handleToggleAllRolePermissions = async (roleId: string, permissionIds: string[], checked: boolean) => {
		const assignedPermissionIds = rolePermissionIds.get(roleId) ?? new Set<string>();
		const permissionIdsToUpdate = permissionIds.filter((permissionId) => assignedPermissionIds.has(permissionId) !== checked);
		const bulkKey = `${roleId}:*`;
		await runPendingAction(
			pendingRolePermissionKeys,
			setPendingRolePermissionKeys,
			bulkKey,
			async () => {
				try {
					for (const permissionId of permissionIdsToUpdate) {
						if (checked) {
							await client.assignPermissionToRole(roleId, permissionId);
						} else {
							await client.unassignPermissionFromRole(roleId, permissionId);
						}
					}
				} finally {
					await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'roles'] });
				}
			},
			(message) => toastService.error(message),
		);
	};

	const handleAddObjectId = async () => {
		const value = objectIdInput.trim();
		if (!guidPattern.test(value) || value === emptyGuid) {
			toastService.error('Enter a valid object id');
			return;
		}

		const name = objectNameInput.trim();
		const type = objectTypeInput.trim();
		setAddingObject(true);
		try {
			if (name || type) {
				await client.updateObject(value, { name: name || undefined, type: type || undefined });
			}

			setObjectIds((current) => (current.some((id) => id.toLowerCase() === value.toLowerCase()) ? current : [...current, value]));
			setObjectNames((current) => (name ? { ...current, [getObjectNameKey(value)]: name } : current));
			setObjectTypes((current) => (type ? { ...current, [getObjectNameKey(value)]: type } : current));
			setObjectIdInput('');
			setObjectNameInput('');
			setObjectTypeInput('');
			await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'object-role-matrix'], refetchType: 'none' });
		} catch (error) {
			toastService.error(getErrorMessage(error));
		} finally {
			setAddingObject(false);
		}
	};

	const handleToggleObjectRole = async (objectId: string, roleId: string, checked: boolean) => {
		const key = getObjectRoleKey(objectId, roleId);
		await runPendingAction(
			pendingObjectRoleKeys,
			setPendingObjectRoleKeys,
			key,
			async () => {
				const objectKey = getObjectNameKey(objectId);
				const objectName = objectNames[objectKey];
				const objectType = objectTypes[objectKey];
				if (checked) {
					await client.assignRoleToObject(objectId, roleId, objectName || objectType ? { name: objectName, type: objectType } : undefined);
				} else {
					await client.unassignRoleFromObject(objectId, roleId);
				}

				queryClient.setQueryData<ObjectRoleMatrixModel>(['permission-admin', 'object-role-matrix', objectIds, objectFilterValue], (current) =>
					current
						? {
								...current,
								objects: current.objects.map((row) =>
									row.objectId.toLowerCase() === objectId.toLowerCase()
										? {
												...row,
												objectName: row.objectName ?? objectName,
												objectType: row.objectType ?? objectType,
												assignedRoleIds: setAssignment(row.assignedRoleIds, roleId, checked),
												roles: row.roles.map((role) => (role.roleId === roleId ? { ...role, isAssigned: checked } : role)),
											}
										: row,
								),
							}
						: current,
				);
				await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'object-role-matrix'], refetchType: 'none' });
			},
			(message) => toastService.error(message),
		);
	};

	const handleToggleUserRole = async (user: RoleMatrixEntry, roleId: string, checked: boolean) => {
		const key = getObjectRoleKey(user.id, roleId);
		await runPendingAction(
			pendingObjectRoleKeys,
			setPendingObjectRoleKeys,
			key,
			async () => {
				if (checked) {
					await client.assignRoleToObject(user.id, roleId, { name: user.name, type: 'User' });
				} else {
					await client.unassignRoleFromObject(user.id, roleId);
				}

				await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'user-role-matrix'] });
			},
			(message) => toastService.error(message),
		);
	};

	const handleToggleAgentRole = async (agent: RoleMatrixEntry, roleId: string, checked: boolean) => {
		const key = getObjectRoleKey(agent.id, roleId);
		await runPendingAction(
			pendingObjectRoleKeys,
			setPendingObjectRoleKeys,
			key,
			async () => {
				if (checked) {
					await client.assignRoleToObject(agent.id, roleId, { name: agent.name, type: 'Agent' });
				} else {
					await client.unassignRoleFromObject(agent.id, roleId);
				}

				await queryClient.invalidateQueries({ queryKey: ['permission-admin', 'agent-role-matrix'] });
			},
			(message) => toastService.error(message),
		);
	};

	const handleObjectSaved = (objectId: string, name?: string, type?: string) => {
		setObjectNames((current) => {
			const next = { ...current };
			if (name) {
				next[getObjectNameKey(objectId)] = name;
			} else {
				delete next[getObjectNameKey(objectId)];
			}
			return next;
		});
		setObjectTypes((current) => {
			const next = { ...current };
			if (type) {
				next[getObjectNameKey(objectId)] = type;
			} else {
				delete next[getObjectNameKey(objectId)];
			}
			return next;
		});

		queryClient.setQueryData<ObjectRoleMatrixModel>(['permission-admin', 'object-role-matrix', objectIds, objectFilterValue], (current) =>
			current
				? {
						...current,
						objects: current.objects.map((row) =>
							row.objectId.toLowerCase() === objectId.toLowerCase()
								? {
										...row,
										objectName: name,
										objectType: type,
									}
								: row,
						),
					}
				: current,
		);
		queryClient.invalidateQueries({ queryKey: ['permission-admin', 'object-role-matrix'], refetchType: 'none' });
	};

	const handleConfirmDelete = async () => {
		if (!deleteTarget) {
			return;
		}

		try {
			if (deleteTarget.kind === 'role') {
				await client.deleteRole(deleteTarget.item.id);
			} else {
				await client.deletePermission(deleteTarget.item.id);
			}
			setDeleteTarget(null);
			refreshAll();
		} catch (error) {
			toastService.error(getErrorMessage(error));
		}
	};

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader title="Permissions" subtitle="Control roles, permissions, and object access." utilityActions={<Tooltip title="Refresh permissions"><IconButton onClick={refreshAll}><Refresh /></IconButton></Tooltip>} statistics={<GalleryStatistics items={[{ label: 'Roles', value: roles.length }, { label: 'Permissions', value: permissions.length }]} />} />

			<Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile sx={{ px: 1 }}>
				<Tab label="Role Permissions" />
				<Tab label="User Roles" />
				<Tab label="Agent Roles" />
			</Tabs>

			{tab === 0 && (
				<Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} sx={{ flexGrow: 1, minHeight: 0, p: 1, overflow: 'auto' }}>
					<Paper sx={{ width: { xs: '100%', lg: 360 }, minWidth: 0, overflow: 'hidden' }}>
						<ManagementList
							kind="role"
							title="Roles"
							rows={roles}
							loading={isLoading}
							filterValue={roleFilter}
							onFilterChange={handleRoleFilterChange}
							onCreate={() => setDialogState({ kind: 'role', mode: 'create' })}
							onEdit={(item) => setDialogState({ kind: 'role', mode: 'edit', item })}
							onDelete={(item) => setDeleteTarget({ kind: 'role', item })}
						/>
					</Paper>
					<Paper sx={{ width: { xs: '100%', lg: 360 }, minWidth: 0, overflow: 'hidden' }}>
						<ManagementList
							kind="permission"
							title="Permissions"
							rows={permissions}
							loading={isLoading}
							filterValue={permissionFilter}
							onFilterChange={handlePermissionFilterChange}
							onCreate={() => setDialogState({ kind: 'permission', mode: 'create' })}
							onEdit={(item) => setDialogState({ kind: 'permission', mode: 'edit', item })}
							onDelete={(item) => setDeleteTarget({ kind: 'permission', item })}
							isMobile={isMobile}
						/>
					</Paper>
					<Paper sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
						<RolePermissionMatrix
							roles={rolePermissionMatrix.roles}
							permissions={rolePermissionMatrix.permissions}
							rolePermissionIds={rolePermissionIds}
							filterValue={rolePermissionMatrixFilter}
							onFilterChange={handleRolePermissionMatrixFilterChange}
							pendingKeys={pendingRolePermissionKeys}
							loading={isLoading}
							onToggle={handleToggleRolePermission}
							onToggleAll={handleToggleAllRolePermissions}
						/>
					</Paper>
				</Stack>
			)}

			{tab === 1 && (
				<Paper sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
						<UserRoleMatrix
							title="User Role Matrix"
							roles={objectRoles}
							entries={userRoleEntries}
							entryLabel="User"
							matrixRows={objectMatrixQuery.data?.objects ?? []}
							pendingKeys={pendingObjectRoleKeys}
							loading={allRolesQuery.isLoading || usersQuery.isLoading || objectMatrixQuery.isLoading}
							onToggle={handleToggleUserRole}
						/>
				</Paper>
			)}

			{tab === 2 && (
				<Paper sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
					<UserRoleMatrix
						title="Agent Role Matrix"
						roles={objectRoles}
							entries={agents}
							entryLabel="Agent"
						matrixRows={agentMatrixQuery.data?.objects ?? []}
						pendingKeys={pendingObjectRoleKeys}
							loading={allRolesQuery.isLoading || agentsQuery.isLoading || agentMatrixQuery.isLoading}
						onToggle={handleToggleAgentRole}
					/>
				</Paper>
			)}

			{dialogState && (
				<EditableItemDialog
					state={dialogState}
					client={client}
					onClose={() => setDialogState(null)}
					onSaved={() => {
						setDialogState(null);
						refreshAll();
					}}
				/>
			)}

			{objectDialog && (
				<ObjectDialog
					state={objectDialog}
					client={client}
					onClose={() => setObjectDialog(null)}
					onSaved={(objectId, name, type) => {
						setObjectDialog(null);
						handleObjectSaved(objectId, name, type);
					}}
				/>
			)}

			<ConfirmDialog
				open={!!deleteTarget}
				title={`Delete ${deleteTarget?.kind ?? 'item'}`}
				message={deleteTarget ? `Delete "${deleteTarget.item.name ?? ''}"?` : ''}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={() => setDeleteTarget(null)}
			/>
		</Stack>
	);
}

function ManagementList({
	kind,
	title,
	rows,
	loading,
	filterValue,
	onFilterChange,
	onCreate,
	onEdit,
	onDelete,
	isMobile = false,
}: {
	kind: EditableKind;
	title: string;
	rows: Array<RoleModel | PermissionModel>;
	loading: boolean;
	filterValue: string;
	onFilterChange: (value: string) => void;
	onCreate: () => void;
	onEdit: (item: RoleModel | PermissionModel) => void;
	onDelete: (item: RoleModel | PermissionModel) => void;
	isMobile?: boolean;
}) {
	const isPermissionList = kind === 'permission';

	return (
		<Stack sx={{ height: '100%', minHeight: 360 }}>
			<Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
					<Typography variant="subtitle1">{title}</Typography>
					<Button size="small" variant="outlined" startIcon={<AddCircle />} onClick={onCreate}>
						Add
					</Button>
				</Stack>
				<TextField size="small" label="Filter" value={filterValue} onChange={(event) => onFilterChange(event.target.value)} fullWidth sx={{ mt: 1 }} />
			</Box>
			<TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Name</TableCell>
							{isPermissionList && !isMobile && <TableCell sx={{ minWidth: 140 }}>Group</TableCell>}
							<TableCell width={96} align="right" />
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.id} hover>
								<TableCell>
									<Typography variant="body2" noWrap title={row.name ?? ''}>
										{row.name}
									</Typography>
									<Typography variant="caption" color="text.secondary" noWrap title={row.description ?? ''}>
										{row.description}
									</Typography>
								</TableCell>
								{isPermissionList && !isMobile && (
									<TableCell>
										<Typography variant="body2" noWrap title={(row as PermissionModel).group ?? ''}>
											{(row as PermissionModel).group}
										</Typography>
									</TableCell>
								)}
								<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
									<RowActions onEdit={() => onEdit(row)} onDelete={() => onDelete(row)} />
								</TableCell>
							</TableRow>
						))}
						{!loading && rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={isPermissionList ? 3 : 2}>
									<Typography variant="body2" color="text.secondary">
										No rows
									</Typography>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
	return (
		<Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
			<Tooltip title="Edit">
				<IconButton size="small" onClick={onEdit}>
					<Edit fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Delete">
				<IconButton size="small" onClick={onDelete}>
					<Delete fontSize="small" />
				</IconButton>
			</Tooltip>
		</Stack>
	);
}

function RolePermissionMatrix({
	roles,
	permissions,
	rolePermissionIds,
	filterValue,
	onFilterChange,
	pendingKeys,
	loading,
	onToggle,
	onToggleAll,
}: {
	roles: RoleModel[];
	permissions: PermissionModel[];
	rolePermissionIds: Map<string, Set<string>>;
	filterValue: string;
	onFilterChange: (value: string) => void;
	pendingKeys: Set<string>;
	loading: boolean;
	onToggle: (roleId: string, permissionId: string, checked: boolean) => Promise<void>;
	onToggleAll: (roleId: string, permissionIds: string[], checked: boolean) => Promise<void>;
}) {
	return (
		<MatrixFrame
			title="Permission Matrix"
			loading={loading}
			empty={roles.length === 0 || permissions.length === 0}
			filterValue={filterValue}
			onFilterChange={onFilterChange}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={{ minWidth: 260 }}>Permission</TableCell>
						{roles.map((role) => {
							const assignedCount = permissions.filter((permission) => rolePermissionIds.get(role.id)?.has(permission.id)).length;
							const allAssigned = permissions.length > 0 && assignedCount === permissions.length;
							const partiallyAssigned = assignedCount > 0 && !allAssigned;
							const hasPendingChanges = pendingKeys.has(`${role.id}:*`) || permissions.some((permission) => pendingKeys.has(getRolePermissionKey(role.id, permission.id)));
							return (
								<TableCell key={role.id} align="center" sx={{ minWidth: 132 }}>
									<Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
										<Typography variant="caption" title={role.name ?? ''} noWrap>
											{role.name}
										</Typography>
										<Tooltip title={allAssigned ? `Deselect all permissions for ${role.name}` : `Select all permissions for ${role.name}`}>
											<span>
												<Checkbox
													size="small"
													checked={allAssigned}
													indeterminate={partiallyAssigned}
													disabled={loading || permissions.length === 0 || hasPendingChanges}
													inputProps={{ 'aria-label': `${allAssigned ? 'Deselect all' : 'Select all'} permissions for ${role.name}` }}
													onChange={(event) => void onToggleAll(role.id, permissions.map((permission) => permission.id), event.target.checked)}
												/>
											</span>
										</Tooltip>
									</Stack>
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				<TableBody>
					{permissions.map((permission) => (
						<TableRow key={permission.id} hover>
							<TableCell>
								<Typography variant="body2" noWrap title={permission.name ?? ''}>
									{permission.name}
								</Typography>
							</TableCell>
							{roles.map((role) => {
								const pending = pendingKeys.has(`${role.id}:*`) || pendingKeys.has(getRolePermissionKey(role.id, permission.id));
								return (
									<TableCell key={role.id} align="center">
										<AssignmentCheckbox
											checked={rolePermissionIds.get(role.id)?.has(permission.id) ?? false}
											pending={pending}
											onChange={(checked) => onToggle(role.id, permission.id, checked)}
										/>
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</MatrixFrame>
	);
}

function UserRoleMatrix({
	title,
	roles,
	entries,
	entryLabel,
	matrixRows,
	pendingKeys,
	loading,
	onToggle,
}: {
	title: string;
	roles: RoleModel[];
	entries: RoleMatrixEntry[];
	entryLabel: string;
	matrixRows: {
		objectId: string;
		roles: { roleId: string; isAssigned: boolean }[];
	}[];
	pendingKeys: Set<string>;
	loading: boolean;
	onToggle: (entry: RoleMatrixEntry, roleId: string, checked: boolean) => Promise<void>;
}) {
	const rowByUserId = useMemo(() => new Map(matrixRows.map((row) => [row.objectId.toLowerCase(), row])), [matrixRows]);

	return (
		<MatrixFrame title={title} loading={loading} empty={entries.length === 0}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={{ minWidth: 320 }}>{entryLabel}</TableCell>
						<RoleHeaderCells roles={roles} />
					</TableRow>
				</TableHead>
				<TableBody>
					{entries.map((entry) => {
						const row = rowByUserId.get(entry.id.toLowerCase());
						return (
							<TableRow key={entry.id} hover>
								<TableCell>
									<Typography variant="body2" noWrap title={entry.name}>
										{entry.name}
									</Typography>
									{entry.description && (
										<Typography variant="caption" color="text.secondary" noWrap title={entry.description}>
											{entry.description}
										</Typography>
									)}
								</TableCell>
								{roles.map((role) => {
									const cell = row?.roles.find((item) => item.roleId === role.id);
									const pending = pendingKeys.has(getObjectRoleKey(entry.id, role.id));
									return (
										<TableCell key={role.id} align="center">
											<AssignmentCheckbox checked={cell?.isAssigned ?? false} pending={pending} onChange={(checked) => onToggle(entry, role.id, checked)} />
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</MatrixFrame>
	);
}

function ObjectRoleMatrix({
	roles,
	objectIds,
	objectNames,
	objectTypes,
	matrixRows,
	filterValue,
	pendingKeys,
	loading,
	onRemoveObject,
	onEditObject,
	onToggle,
}: {
	roles: RoleModel[];
	objectIds: string[];
	objectNames: Record<string, string | undefined>;
	objectTypes: Record<string, string | undefined>;
	matrixRows: {
		objectId: string;
		objectName?: string | undefined;
		objectType?: string | undefined;
		roles: { roleId: string; isAssigned: boolean }[];
	}[];
	filterValue: string;
	pendingKeys: Set<string>;
	loading: boolean;
	onRemoveObject: (objectId: string) => void;
	onEditObject: (objectId: string, name: string, type: string) => void;
	onToggle: (objectId: string, roleId: string, checked: boolean) => Promise<void>;
}) {
	const rowByObjectId = useMemo(() => new Map(matrixRows.map((row) => [row.objectId.toLowerCase(), row])), [matrixRows]);
	const visibleObjectIds = useMemo(
		() => matrixRows.filter((row) => matchesObjectRow(row, objectNames, objectTypes, filterValue)).map((row) => row.objectId),
		[matrixRows, objectNames, objectTypes, filterValue],
	);
	const manualObjectIds = useMemo(() => new Set(objectIds.map((id) => id.toLowerCase())), [objectIds]);

	return (
		<MatrixFrame title="Object Matrix" loading={loading} empty={visibleObjectIds.length === 0 || roles.length === 0}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell sx={{ minWidth: 420 }}>Object</TableCell>
						<RoleHeaderCells roles={roles} />
					</TableRow>
				</TableHead>
				<TableBody>
					{visibleObjectIds.map((objectId) => {
						const row = rowByObjectId.get(objectId.toLowerCase());
						const isManualObjectId = manualObjectIds.has(objectId.toLowerCase());
						const objectKey = getObjectNameKey(objectId);
						const objectName = row?.objectName || objectNames[objectKey];
						const objectType = row?.objectType || objectTypes[objectKey];
						return (
							<TableRow key={objectId} hover>
								<TableCell>
									<Stack direction="row" alignItems="center" spacing={1}>
										<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
											<Tooltip title="Edit object">
												<IconButton size="small" onClick={() => onEditObject(objectId, objectName ?? '', objectType ?? '')}>
													<Edit fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Remove">
												<span>
													<IconButton size="small" disabled={!isManualObjectId} onClick={() => onRemoveObject(objectId)}>
														<Delete fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</Stack>
										<Stack sx={{ minWidth: 0 }}>
											<Typography variant="body2" noWrap title={objectName || objectId}>
												{objectName || objectId}
											</Typography>
											<Typography variant="caption" color="text.secondary" noWrap title={objectId}>
												{objectName ? objectId : ''}
											</Typography>
											<Typography variant="caption" color="text.secondary" noWrap title={objectType ?? ''}>
												{objectType}
											</Typography>
										</Stack>
									</Stack>
								</TableCell>
								{roles.map((role) => {
									const cell = row?.roles.find((item) => item.roleId === role.id);
									const pending = pendingKeys.has(getObjectRoleKey(objectId, role.id));
									return (
										<TableCell key={role.id} align="center">
											<AssignmentCheckbox checked={cell?.isAssigned ?? false} pending={pending} onChange={(checked) => onToggle(objectId, role.id, checked)} />
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</MatrixFrame>
	);
}

function RoleHeaderCells({ roles }: { roles: RoleModel[] }) {
	return (
		<>
			{roles.map((role) => (
				<TableCell key={role.id} align="center" sx={{ minWidth: 132 }}>
					<Typography variant="caption" title={role.name ?? ''} noWrap>
						{role.name}
					</Typography>
				</TableCell>
			))}
		</>
	);
}

function AssignmentCheckbox({ checked, pending, onChange }: { checked: boolean; pending: boolean; onChange: (checked: boolean) => void }) {
	return (
		<Checkbox
			size="small"
			checked={checked}
			disabled={pending}
			icon={pending ? <CircularProgress size={16} /> : undefined}
			checkedIcon={pending ? <CircularProgress size={16} /> : undefined}
			onChange={(event) => onChange(event.target.checked)}
		/>
	);
}

function MatrixFrame({
	title,
	loading,
	empty,
	filterValue,
	onFilterChange,
	children,
}: {
	title: string;
	loading: boolean;
	empty: boolean;
	filterValue?: string;
	onFilterChange?: (value: string) => void;
	children: React.ReactNode;
}) {
	return (
		<Stack sx={{ height: '100%', minHeight: 360 }}>
			<Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
					<Typography variant="subtitle1">{title}</Typography>
					<Stack direction="row" alignItems="center" spacing={1}>
						{onFilterChange && (
							<TextField
								size="small"
								label="Matrix filter"
								value={filterValue ?? ''}
								onChange={(event) => onFilterChange(event.target.value)}
								sx={{ minWidth: 220 }}
							/>
						)}
						{loading && (
							<Typography variant="caption" color="text.secondary">
								Loading
							</Typography>
						)}
					</Stack>
				</Stack>
			</Box>
			<TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>{empty && !loading ? <EmptyMatrix /> : children}</TableContainer>
		</Stack>
	);
}

function EmptyMatrix() {
	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="body2" color="text.secondary">
				No rows
			</Typography>
		</Box>
	);
}

function ObjectDialog({
	state,
	client,
	onClose,
	onSaved,
}: {
	state: ObjectDialogState;
	client: PermissionAdministrationClient;
	onClose: () => void;
	onSaved: (objectId: string, name?: string, type?: string) => void;
}) {
	const toastService = useToastService();
	const [name, setName] = useState(state.name);
	const [type, setType] = useState(state.type);
	const [submitting, setSubmitting] = useState(false);

	const handleSave = async () => {
		const trimmedName = name.trim();
		const trimmedType = type.trim();

		setSubmitting(true);
		try {
			await client.updateObject(state.objectId, { name: trimmedName || undefined, type: trimmedType || undefined });
			onSaved(state.objectId, trimmedName || undefined, trimmedType || undefined);
		} catch (error) {
			toastService.error(getErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit object</DialogTitle>
			<DialogContent>
				<TextField margin="dense" label="Object id" fullWidth value={state.objectId} disabled />
				<TextField autoFocus margin="dense" label="Name" fullWidth value={name} onChange={(event) => setName(event.target.value)} disabled={submitting} />
				<TextField select margin="dense" label="Type" fullWidth value={type} onChange={(event) => setType(event.target.value)} disabled={submitting}>
					<MenuItem value="">None</MenuItem>
					{objectTypeOptions.map((option) => (
						<MenuItem key={option} value={option}>
							{option}
						</MenuItem>
					))}
				</TextField>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} loading={submitting}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function EditableItemDialog({
	state,
	client,
	onClose,
	onSaved,
}: {
	state: EditableDialogState;
	client: PermissionAdministrationClient;
	onClose: () => void;
	onSaved: () => void;
}) {
	const toastService = useToastService();
	const [name, setName] = useState(state.mode === 'edit' ? (state.item.name ?? '') : '');
	const [group, setGroup] = useState(state.mode === 'edit' && state.kind === 'permission' ? ((state.item as PermissionModel).group ?? '') : '');
	const [description, setDescription] = useState(state.mode === 'edit' ? (state.item.description ?? '') : '');
	const [submitting, setSubmitting] = useState(false);

	const title = `${state.mode === 'create' ? 'Create' : 'Edit'} ${state.kind}`;

	const handleSave = async () => {
		const trimmedName = name.trim();
		if (!trimmedName) {
			toastService.error('Name is required');
			return;
		}

		const trimmedGroup = group.trim();
		if (state.kind === 'permission' && !trimmedGroup) {
			toastService.error('Group is required');
			return;
		}

		setSubmitting(true);
		try {
			if (state.kind === 'role') {
				if (state.mode === 'create') {
					await client.createRole({ name: trimmedName, description: description.trim(), permissionIds: [] });
				} else {
					const role = state.item as RoleModel;
					await client.updateRole(role.id, {
						id: role.id,
						name: trimmedName,
						description: description.trim(),
						permissionIds: role.permissionIds ?? [],
					});
				}
			} else if (state.mode === 'create') {
				await client.createPermission({
					name: trimmedName,
					group: trimmedGroup,
					description: description.trim(),
				});
			} else {
				await client.updatePermission(state.item.id, {
					id: state.item.id,
					name: trimmedName,
					group: trimmedGroup,
					description: description.trim(),
				});
			}
			onSaved();
		} catch (error) {
			toastService.error(getErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<TextField autoFocus margin="dense" label="Name" fullWidth value={name} onChange={(event) => setName(event.target.value)} disabled={submitting} />
				{state.kind === 'permission' && (
					<TextField margin="dense" label="Group" fullWidth value={group} onChange={(event) => setGroup(event.target.value)} disabled={submitting} />
				)}
				<TextField
					margin="dense"
					label="Description"
					fullWidth
					multiline
					rows={3}
					value={description}
					onChange={(event) => setDescription(event.target.value)}
					disabled={submitting}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} disabled={!name.trim()} loading={submitting}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}

function getErrorMessage(error: unknown) {
	return error instanceof Error ? error.message : 'Request failed';
}

async function getAllUsers(client: UserClient): Promise<TenantUserModel[]> {
	const pageSize = 100;
	const users: TenantUserModel[] = [];
	let pageIndex = 0;
	let hasNextPage = true;

	while (hasNextPage) {
		const page = await client.getUsers(pageIndex, pageSize, undefined);
		users.push(...page.items);
		hasNextPage = page.hasNextPage;
		pageIndex++;
	}

	return users;
}

async function getAllAgents(client: AgentClient): Promise<AgentModel[]> {
	const pageSize = 100;
	const agents: AgentModel[] = [];
	let pageIndex = 0;
	let hasNextPage = true;

	while (hasNextPage) {
		const page = await client.getAgents(pageIndex, pageSize, undefined);
		agents.push(...page.items);
		hasNextPage = page.hasNextPage;
		pageIndex++;
	}

	return agents;
}
